import { Helmet } from "react-helmet-async";
import { BRAND } from "@/constants/brand";

interface SEOProps {
  /** Título da página (será formatado com template) */
  title?: string;
  /** Descrição da página */
  description?: string;
  /** Keywords adicionais (serão mescladas com as padrão) */
  keywords?: string[];
  /** URL da imagem para Open Graph */
  image?: string;
  /** Tipo de página para Open Graph */
  type?: "website" | "article";
  /** URL canônica da página */
  canonicalUrl?: string;
  /** Dados estruturados JSON-LD */
  jsonLd?: Record<string, unknown>;
  /** Não indexar esta página */
  noIndex?: boolean;
}

export function SEO({
  title,
  description = BRAND.seo.description,
  keywords = [],
  image = BRAND.assets.ogImage,
  type = "website",
  canonicalUrl,
  jsonLd,
  noIndex = false,
}: SEOProps) {
  // Formata o título usando o template
  const formattedTitle = title
    ? BRAND.seo.titleTemplate.replace("%s", title)
    : BRAND.seo.title;

  // Mescla keywords padrão com as específicas da página
  const allKeywords = [...BRAND.seo.keywords, ...keywords].join(", ");

  return (
    <Helmet>
      {/* Título */}
      <title>{formattedTitle}</title>

      {/* Meta tags básicas */}
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <meta name="author" content={BRAND.legal.author} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* URL canônica */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* JSON-LD estruturado */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

/**
 * JSON-LD padrão para a organização/site
 */
export const defaultOrganizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: BRAND.name,
  description: BRAND.seo.description,
  url: typeof window !== "undefined" ? window.location.origin : "",
  logo: BRAND.assets.logo,
};
